import { useMemo } from 'react'
import { t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { CellContext, createColumnHelper, PaginationState } from '@tanstack/react-table'
import { Box, Chip, IconButton, Stack, Tooltip } from '@mui/material'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded'
import dayjs from 'dayjs'

import { Table } from '@/components/ui/tables/Table'
import { TimeSession, TimeSessionStaff } from '@/core/api/time-tracking'
import { formatDuration, closureTypeLabel } from '../lib/format'

type SessionRow = TimeSession & { id: string }

type SessionsTableProps = {
  sessions: TimeSession[]
  loading: boolean
  onCorrect: (session: TimeSession) => void
  onViewHistory?: (session: TimeSession) => void
  isAdminView?: boolean
  totalRows: number
  onPaginationChange: (state: PaginationState) => void
}

const tabularNumStyle = { fontVariantNumeric: 'tabular-nums' as const }

const columnHelper = createColumnHelper<SessionRow>()

export function SessionsTable({ sessions, loading, onCorrect, onViewHistory, isAdminView = false, totalRows, onPaginationChange }: SessionsTableProps) {
  useLingui()

  const data: SessionRow[] = useMemo(() => sessions.map((s) => ({ ...s, id: s._id })), [sessions])

  const columns = useMemo(
    () => [
      ...(isAdminView ? [columnHelper.accessor((row) => row.staff, { id: 'staff', header: t`Staff`, cell: StaffCell })] : []),
      columnHelper.accessor('startTime', { header: t`Date`, cell: DateCell }),
      columnHelper.accessor('startTime', { id: 'start', header: t`Start`, cell: StartTimeCell }),
      columnHelper.accessor('endTime', { header: t`End`, cell: EndTimeCell }),
      columnHelper.accessor('duration', { header: t`Duration`, cell: DurationCell }),
      columnHelper.accessor('closureType', { header: t`Closure`, cell: ClosureTypeCell }),
      columnHelper.accessor('source', { header: t`Source`, cell: SourceCell }),
      columnHelper.display({
        id: 'actions',
        header: t`Actions`,
        cell: (props) => <ActionsCell {...props} isAdminView={isAdminView} onCorrect={onCorrect} onViewHistory={onViewHistory} />,
      }),
    ],
    [isAdminView, onCorrect, onViewHistory]
  )

  return (
    <Box
      sx={{
        '& .MuiTableContainer-root': { boxShadow: 'none', borderRadius: 0 },
        '& .MuiTableHead-root .MuiTableCell-root': {
          bgcolor: 'grey.50',
          color: 'text.secondary',
          fontWeight: 700,
          fontSize: '0.75rem',
          textTransform: 'uppercase',
          letterSpacing: 0.5,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        },
        '& .MuiTableBody-root .MuiTableRow-root': {
          transition: 'background-color 150ms ease',
          '&:hover': { bgcolor: 'grey.50' },
        },
        '& .MuiTableBody-root .MuiTableCell-root': {
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          fontSize: '0.875rem',
        },
      }}
    >
      <Table
        columns={columns}
        data={data}
        loading={loading}
        disableGlobalSearch
        showPagination
        manualPagination
        autoResetPage={false}
        rowsPerPageOptions={[5, 10, 15]}
        totalRows={totalRows}
        onPaginationChange={onPaginationChange}
      />
    </Box>
  )
}

function DateCell({ getValue }: CellContext<SessionRow, string>) {
  return (
    <Box component="span" sx={tabularNumStyle}>
      {dayjs(getValue()).format('DD/MM/YYYY')}
    </Box>
  )
}

function StartTimeCell({ getValue }: CellContext<SessionRow, string>) {
  return (
    <Box component="span" sx={tabularNumStyle}>
      {dayjs(getValue()).format('HH:mm')}
    </Box>
  )
}

function EndTimeCell({ getValue }: CellContext<SessionRow, string | null>) {
  const val = getValue()
  if (val) {
    return (
      <Box component="span" sx={tabularNumStyle}>
        {dayjs(val).format('HH:mm')}
      </Box>
    )
  }
  return <Chip label={t`Open`} size="small" sx={{ bgcolor: '#fee2e2', color: '#991b1b', fontWeight: 600, height: 22, '& .MuiChip-label': { px: 1.25 } }} />
}

function DurationCell({ getValue }: CellContext<SessionRow, number | null>) {
  return (
    <Box component="span" sx={{ ...tabularNumStyle, fontWeight: 500 }}>
      {formatDuration(getValue())}
    </Box>
  )
}

function ClosureTypeCell({ getValue }: CellContext<SessionRow, string>) {
  return (
    <Box component="span" sx={{ color: 'text.secondary' }}>
      {closureTypeLabel(getValue())}
    </Box>
  )
}

function SourceCell({ getValue }: CellContext<SessionRow, string>) {
  useLingui()
  const val = getValue()
  if (val === 'correction_doctor') {
    return (
      <Chip label={t`Modified`} size="small" sx={{ bgcolor: '#dbeafe', color: '#1e40af', fontWeight: 600, height: 22, '& .MuiChip-label': { px: 1.25 } }} />
    )
  }
  if (val === 'correction_admin') {
    return <Chip label={t`Admin`} size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600, height: 22, '& .MuiChip-label': { px: 1.25 } }} />
  }
  return (
    <Box component="span" sx={{ color: 'text.disabled' }}>
      —
    </Box>
  )
}

function StaffCell({ getValue }: CellContext<SessionRow, TimeSessionStaff>) {
  useLingui()
  const staff = getValue()
  const name = `${staff?.userInfo?.name ?? ''} ${staff?.userInfo?.surname ?? ''}`.trim()
  const roleLabel = staff?.status === 'MEDECIN' ? t`Doctor` : staff?.status === 'PDS' ? t`Healthcare pro` : ''
  const isDeactivated = staff?.active === false

  return (
    <Stack spacing={0.25}>
      <Stack direction="row" spacing={1} alignItems="center">
        <Box component="span" sx={{ fontWeight: 500, color: isDeactivated ? 'text.disabled' : 'text.primary' }}>
          {name || '—'}
        </Box>
        {isDeactivated && (
          <Chip
            label={t`Deactivated`}
            size="small"
            sx={{ bgcolor: 'grey.200', color: 'text.secondary', fontWeight: 600, height: 20, '& .MuiChip-label': { px: 1 } }}
          />
        )}
      </Stack>
      {roleLabel && (
        <Box component="span" sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
          {roleLabel}
        </Box>
      )}
    </Stack>
  )
}

function ActionsCell({
  row: { original: session },
  isAdminView,
  onCorrect,
  onViewHistory,
}: CellContext<SessionRow, unknown> & {
  isAdminView: boolean
  onCorrect: (session: TimeSession) => void
  onViewHistory?: (session: TimeSession) => void
}) {
  useLingui()
  const sevenDaysAgo = dayjs().subtract(7, 'day')
  const canCorrect = isAdminView || dayjs(session.startTime).isAfter(sevenDaysAgo)

  return (
    <Stack direction="row" spacing={0.5}>
      {canCorrect && (
        <Tooltip title={t`Correct session`}>
          <IconButton
            size="small"
            onClick={() => onCorrect(session)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
          >
            <EditRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {onViewHistory && (
        <Tooltip title={t`View history`}>
          <IconButton
            size="small"
            onClick={() => onViewHistory(session)}
            sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', bgcolor: 'action.hover' } }}
          >
            <HistoryRoundedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Stack>
  )
}
